import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, buttonClass, Card } from "@promptgatewaysadmin/ui";
import { useSubscriptionState, useInvoices, invalidateAfterCheckout } from "../../queries/hooks.js";
import { useSession } from "../../auth/SessionProvider.js";
import { POLL } from "../../queries/keys.js";
import { money } from "../../components/format.js";

type Phase = "confirming" | "confirmed" | "pending";

/**
 * /checkout/success — the webhook race.
 *
 * Stripe redirects IMMEDIATELY; checkout.session.completed arrives asynchronously. So the
 * customer very often lands here while the subscription still says trialing or incomplete.
 * Three states, one screen:
 *
 *   A · Confirming   always the initial state. Poll at 1s, 2s, 3s, 5s, 8s.
 *   B · Confirmed    the receipt.
 *   C · Still pending after ~20s.
 *
 * STATE C IS NOT AN ERROR. "Payment received" is accurate — Stripe only redirects on a
 * completed session — and "activating" is the honest description of what is outstanding.
 * Never show a failure here: the customer has paid.
 *
 * Rendering the receipt first and verifying after would flash a forged success, which is why
 * A is always first.
 */
export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const qc = useQueryClient();
  const { orgId } = useSession();
  const sub = useSubscriptionState();
  const invoices = useInvoices(true);

  const [phase, setPhase] = useState<Phase>("confirming");
  const attempt = useRef(0);

  // The parameter is Stripe's, and it is verified SERVER-SIDE against the caller's org
  // before anything renders. The portal does not decode it, does not display it, and does
  // not treat its presence as evidence of anything. Missing -> straight home.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const step = () => {
      if (cancelled) return;
      void qc.invalidateQueries().then(() => {
        if (cancelled) return;
        const i = attempt.current++;
        if (i < POLL.checkoutSuccess.length) {
          setTimeout(step, POLL.checkoutSuccess[i]!);
        } else {
          setPhase((p) => (p === "confirmed" ? p : "pending"));
        }
      });
    };
    const t = setTimeout(step, POLL.checkoutSuccess[0]!);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [sessionId, qc]);

  useEffect(() => {
    if (sub.data?.status === "active") {
      setPhase("confirmed");
      invalidateAfterCheckout(qc, orgId!);
    }
  }, [sub.data?.status, qc, orgId]);

  if (!sessionId) return <Navigate to="/" replace />;

  if (phase === "confirming") {
    return (
      <Card title="Confirming your payment">
        <p className="pg-card__body" role="status">
          This usually takes a few seconds.
        </p>
      </Card>
    );
  }

  if (phase === "pending") {
    return (
      <Card title="✓ Payment received">
        <p className="pg-card__body" role="status">
          We&apos;re still activating your plan — this can take a minute. You can head to your
          dashboard; it&apos;ll update itself.
        </p>
        <div className="pg-form__actions">
          <Button variant="secondary" onClick={() => void qc.invalidateQueries()}>
            Check again
          </Button>
          <Link className={buttonClass("primary")} to="/">
            Go to dashboard
          </Link>
        </div>
      </Card>
    );
  }

  const latest = invoices.data?.pages[0]?.data[0];

  return (
    <Card title="You're all set">
      <p className="pg-card__body" role="status">
        Your plan is active. Everyone on your team has access right away.
      </p>
      {latest && (
        <dl className="pg-summary">
          <div>
            <dt>Amount paid</dt>
            <dd>{money(latest.amountPaid ?? latest.amountDue, latest.currency)}</dd>
          </div>
          {/* Stripe's own invoice number, never an invented format: an internal number
              matches nothing in Stripe, the customer's bank statement, or their
              accountant's records. */}
          {latest.number && (
            <div>
              <dt>Receipt no.</dt>
              <dd>{latest.number}</dd>
            </div>
          )}
        </dl>
      )}
      <div className="pg-form__actions">
        {/* Hidden entirely when pdfUrl is null — a dead link is a bad first impression. */}
        {latest?.pdfUrl && (
          <a className={buttonClass("secondary")} href={latest.pdfUrl} target="_blank" rel="noopener noreferrer">
            Download receipt (PDF) ↗
          </a>
        )}
        <Link className={buttonClass("primary")} to="/">
          Go to dashboard
        </Link>
      </div>
    </Card>
  );
}
